import React from 'react';
import { Accordion as MuiAccordion, AccordionDetails, AccordionSummary as MuiAccordionSummary, Typography, styled, AccordionProps, AccordionSummaryProps, accordionSummaryClasses, Box, ButtonBase, accordionClasses } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { FilterCountChip } from './filter-count-chip';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&::before': {
    display: 'none',
  },
  [`&.${accordionClasses.disabled}`]: {
    backgroundColor: 'transparent',
  }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ExpandMore sx={{ fontSize: '20px' }} />}
    {...props}
  />
))(({ theme }) => ({
  position: 'sticky',
  top: 0,
  bottom: 0,
  flexDirection: 'row-reverse',
  backgroundColor: theme.palette.background.paper,
  zIndex: 100,
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
  {
    transform: 'rotate(180deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  [`&.${accordionSummaryClasses.expanded}`]: {
    boxShadow: '0px 4px 8px -2px rgba(0, 0, 0, 0.2)',
  },
}));

type FilterAccordionProps = {
  id: string;
  title: string;
  valueLabels?: string[];
  children: React.ReactNode;
  onClear?: () => void;
  hideClearButton?: boolean;
  hideCount?: boolean;
  disabled?: boolean;
};

export const FilterAccordion: React.FC<FilterAccordionProps> = props => {
  const { classes } = useStyles();
  const { open, setOpen } = React.useContext(FilterAccordionGroupContext);
  const expanded = open === props.id;

  return (
    <Accordion
      onChange={(_e, expanded) => setOpen(expanded ? props.id : null)}
      expanded={expanded}
      disabled={props.disabled}
      id={props.id}
    >
      <AccordionSummary>
        <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
          <Box flex={1} minWidth={0} width="100%">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>{props.title}</Typography>
              {!props.hideCount && !!props.valueLabels?.length && <FilterCountChip count={props.valueLabels.length} />}
            </Box>
            {!!props.valueLabels.length && (
              <Box position="relative" height="20px">
                <Box position="absolute" sx={{ inset: 0 }} width="100%" height="100%">
                  <Typography variant="body2" noWrap>
                    {props.valueLabels?.join(', ')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          {!props.hideClearButton && (
            <ButtonBase
              className={classes.clearAll}
              onClick={(e) => {
                e.stopPropagation();
                if (props.onClear) {
                  props.onClear();
                }
              }}
            >
              Clear
            </ButtonBase>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles()((theme) => ({
  clearAll: {
    zIndex: 1,
    textDecoration: 'underline',
    padding: theme.spacing(1, 0)
  },
}));

type FilterAccordionGroupContextType = {
  open: string | null;
  setOpen: (id: string | null) => void;
};

const FilterAccordionGroupContext = React.createContext<FilterAccordionGroupContextType>({
  open: null,
  setOpen: () => void {},
});

export const FilterAccordionGroup: React.FC = props => {
  const [ open, setOpen ] = React.useState<string | null>(null);

  return (
    <FilterAccordionGroupContext.Provider value={{ open, setOpen }}>
      {props.children}
    </FilterAccordionGroupContext.Provider>
  );
};